import React, { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, useNavigate } from "react-router-dom";
import { Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Carriers from "./Pages/Carriers";
import Home from "./Pages/Home";
import Dashboard1 from "./Pages/Dashboard1";
import { Provider, useDispatch, useSelector } from "react-redux";
import { store, persistor } from "./store/store";
import { PersistGate } from 'redux-persist/integration/react';
import CarrierDetail from './Pages/CarrierDetail'
import Carrier from "./Pages/Carrier";
import LoginPage from "./Pages/Login";
import NotFound from "./Pages/NotFound";
import { resetUser } from "./slice/userSlice";
import { COLORS } from "./constants";

// Define a custom theme
const generateTheme = (role) => {
  let primaryColor = COLORS.primaryBlue;
  let secondaryColor = COLORS.primaryLightBlue;

  if (role === "customer") {
    primaryColor = COLORS.primaryGreen;
    secondaryColor = COLORS.primaryLightGreen;
  }

  return createTheme({
    palette: {
      primary: {
        main: primaryColor,
      },
      secondary: {
        main: secondaryColor,
      },
    },
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: COLORS.black,
          },
        },
      },
    },
  });
};

const ProtectedRoute = () => {
  const token = useSelector(state => state.user.user.token);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isTokenValid = () => {
    if (!token) return false;

    try {
      const { exp } = JSON.parse(atob(token.split(".")[1])); 
      return Date.now() < exp * 1000; 
    } catch {
      return false; 
    }
  };

  useEffect(() => {
    if (!isTokenValid()) {
      dispatch(resetUser());
      navigate("/login", { replace: true });
    }

    const checkTokenInterval = setInterval(() => {
      if (!isTokenValid()) {
        dispatch(resetUser());
        navigate("/login", { replace: true });
      }
    }, 1000); 

    return () => clearInterval(checkTokenInterval); 
  }, [navigate, token, dispatch]);

  return isTokenValid() ? <Outlet /> : <Navigate to="/login" replace />;
};

const ThemedApp = () => {
  const userRole = useSelector(state => state.user.userRole);
  const theme = generateTheme(userRole);

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <Routes>
          <Route path='/login' element={<LoginPage />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Home />}>
              <Route index element={<Dashboard1 />} />
              <Route path='carriers' element={<Carrier />}>
                <Route index element={<Carriers />} />
                <Route path="details/:carrierId" element={<CarrierDetail />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemedApp />
      </PersistGate>
    </Provider>
  );
}

export default App;